import { apiFetch, apiFetchStream } from '../../lib/api';
import type {
  SkillsNodeStatus,
  SkillsNodeType,
  SkillsTreeNode,
  SkillsTreeOperator,
} from './types';

type OrganizationTreeNodeResponse = {
  id: string;
  parentId: string | null;
  type: SkillsNodeType;
  title: string;
  status: SkillsNodeStatus;
  meta: Record<string, unknown>;
  rules?: Record<string, unknown>;
  sortOrder?: number;
};

type OrganizationTreeResponse = {
  orgId: string;
  nodes: OrganizationTreeNodeResponse[];
};

export type OrganizationTreeAuditInvalidNode = {
  nodeId: string;
  parentId: string | null;
  nodeType: SkillsNodeType;
  title: string;
  teamLabel: string;
  designationType: string;
  serviceCode: string;
  tierSkuId: string | null;
  routeLevel: 'service' | 'tier';
};

export type OrganizationTreeAuditResponse = {
  orgId: string;
  summary: {
    totalNodes: number;
    activeNodes: number;
    routedNodes: number;
    validRoutedNodes: number;
    invalidRoutedNodes: number;
    duplicateRouteGroups: number;
  };
  invalidRoutedNodes: OrganizationTreeAuditInvalidNode[];
  duplicateRoutes: Array<{
    routeKey: string;
    serviceCode: string;
    tierSkuId: string | null;
    routeLevel: 'service' | 'tier';
    nodes: Array<{
      nodeId: string;
      title: string;
      teamLabel: string | null;
      designationType: string | null;
    }>;
  }>;
};

type OrganizationTreeNodeMutationResponse = {
  orgId: string;
  node: OrganizationTreeNodeResponse;
};

type OrganizationTreeNodeDeleteResponse = {
  ok: boolean;
  orgId: string;
  deletedNodeIds: string[];
  deletedCount: number;
};

type AdminCatalogTier = {
  skuId: string;
  name: string;
  status?: 'draft' | 'published' | 'archived' | string;
  isPrimaryService?: boolean;
  billingType?: 'recurring' | 'one_time' | string | null;
};

type AdminCatalogService = {
  code: string;
  title: string;
  status?: 'draft' | 'published' | 'archived' | string;
  tiers?: AdminCatalogTier[];
};

type AdminCatalogConfigResponse = {
  services: AdminCatalogService[];
};

type AdminUserRole = 'admin' | 'agent' | 'client' | 'candidate';

type AdminUserRecord = {
  id: string;
  email: string;
  name?: string | null;
  avatarUrl?: string | null;
  photoURL?: string | null;
  photoUrl?: string | null;
  profileImageUrl?: string | null;
  imageUrl?: string | null;
  role: AdminUserRole;
};

type AdminUsersResponse = {
  users: AdminUserRecord[];
};

export type ServiceRoutingTierOption = {
  skuId: string;
  name: string;
};

export type ServiceRoutingOption = {
  serviceCode: string;
  serviceTitle: string;
  tiers: ServiceRoutingTierOption[];
};

export type BrainAIAgentDefinition = {
  id: string;
  code: string;
  title: string;
  description: string;
  roleSummary: string;
  systemPrompt: string;
  defaultProvider: string;
  defaultModel: string | null;
  capabilities: string[];
  allowedTools: string[];
  requiresHumanApproval: boolean;
  status: string;
  metadata: Record<string, unknown> | null;
};

export type BrainAIAgentAssignment = {
  id: string;
  agentId: string;
  agentCode: string;
  agentTitle: string;
  agentMetadata: Record<string, unknown> | null;
  agentSurface: string | null;
  planningRole: string | null;
  planningDomain: string | null;
  scopeType: string;
  orgId: string | null;
  nodeId: string | null;
  serviceId: string | null;
  active: boolean;
  priority: number;
  metadata: Record<string, unknown> | null;
};

export type BrainKBaseProcessOption = {
  id: string;
  title: string;
  status: string;
  processType: string;
  serviceCode: string | null;
  serviceLabel: string | null;
  category: string | null;
  subcategories: string[];
};

export type BrainNodeRosterMember = {
  userId: string;
  uid: string;
  email: string | null;
  name: string;
  photoURL: string | null;
  platformRole: string | null;
  title: string | null;
  membershipRole: string;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
};

export type BrainNodeRosterResponse = {
  orgId: string;
  nodeId: string;
  nodeTitle: string;
  members: BrainNodeRosterMember[];
};

export type BrainProjectManagerApproval = {
  id: string;
  status: string;
  approvalType: string;
  resourceType: string | null;
  resourceId: string | null;
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
  requestedBy:
    | {
        userId: string;
        uid: string;
        name: string;
        email: string | null;
      }
    | null;
  reviewedBy:
    | {
        userId: string;
        uid: string;
        name: string;
        email: string | null;
      }
    | null;
  proposal: Record<string, unknown> | null;
  appliedSummary: Record<string, unknown> | null;
  phaseKey?: string | null;
  phaseLabel?: string | null;
  phaseAction?: string | null;
  nextPhaseKey?: string | null;
  nextPhaseLabel?: string | null;
  scope: {
    nodeId: string | null;
    orgId: string | null;
    orgName?: string | null;
    processTitle: string | null;
  };
};

export type BrainProjectManagerToolExecution = {
  id: string;
  toolName: string;
  status: string;
  inputPayload: Record<string, unknown> | null;
  outputPayload: Record<string, unknown> | null;
  errorText: string | null;
  createdAt: string;
  completedAt: string | null;
};

export type BrainProjectManagerChildRun = {
  id: string;
  status: string;
  agentCode: string | null;
  agentTitle: string | null;
  parentRunId: string | null;
  rootRunId: string | null;
  summary: string | null;
  errorText: string | null;
  delegationMetadata: Record<string, unknown> | null;
  createdAt: string;
  completedAt: string | null;
};

export type BrainProjectManagerRun = {
  id: string;
  agentCode: string | null;
  agentTitle: string | null;
  status: string;
  parentRunId: string | null;
  rootRunId: string | null;
  provider: string;
  model: string;
  inputTokens: number | null;
  outputTokens: number | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  nodeId: string | null;
  orgId: string | null;
  orgName?: string | null;
  summary: string | null;
  errorText: string | null;
  outputText: string | null;
  delegationMetadata: Record<string, unknown> | null;
  promptSnapshot: Record<string, unknown> | null;
  responsePayload: Record<string, unknown> | null;
  requestedBy:
    | {
        userId: string;
        uid: string;
        name: string;
        email: string | null;
      }
    | null;
  approval: BrainProjectManagerApproval | null;
  toolExecutions: BrainProjectManagerToolExecution[];
  childRuns: BrainProjectManagerChildRun[];
};

export type BrainProjectManagerStreamEvent =
  | {
      type: 'status';
      stage: string;
      message: string;
      detail?: string | null;
    }
  | {
      type: 'complete';
      response: {
        run: BrainProjectManagerRun;
      };
    }
  | {
      type: 'error';
      error: string;
    };

export type BrainProjectManagerNodeClient = {
  orgId: string;
  name: string;
  ownerEmail: string | null;
  assignedServiceTeam: string | null;
  assignedServiceTeams: string[];
  primaryServiceActive: boolean | null;
  serviceLabels: string[];
  projectCountOnNode: number;
  latestProjectUpdatedAt: string | null;
  latestRunAt: string | null;
  latestSubscriptionUpdatedAt: string | null;
  sources: string[];
};

function normalizeNode(node: OrganizationTreeNodeResponse): SkillsTreeNode {
  return {
    id: node.id,
    parentId: node.parentId,
    type: node.type,
    title: node.title,
    status: node.status,
    meta: node.meta ?? {},
    rules: node.rules ?? {},
  };
}

export async function fetchOrganizationTree(orgId?: string): Promise<{ orgId: string; nodes: SkillsTreeNode[] }> {
  const query = orgId ? `?orgId=${encodeURIComponent(orgId)}` : '';
  const response = await apiFetch<OrganizationTreeResponse>(`/api/admin/organization-tree${query}`);
  return {
    orgId: response.orgId,
    nodes: (response.nodes ?? []).map(normalizeNode),
  };
}

export async function fetchOrganizationTreeAudit(orgId?: string) {
  const query = orgId ? `?orgId=${encodeURIComponent(orgId)}` : '';
  return apiFetch<OrganizationTreeAuditResponse>(`/api/admin/organization-tree/audit${query}`);
}

export async function createOrganizationTreeNode(payload: {
  parentId: string;
  type?: SkillsNodeType;
  title?: string;
  status?: SkillsNodeStatus;
  meta?: Record<string, unknown>;
  rules?: Record<string, unknown>;
  sortOrder?: number;
  orgId?: string;
}) {
  const response = await apiFetch<OrganizationTreeNodeMutationResponse>('/api/admin/organization-tree/nodes', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  return {
    orgId: response.orgId,
    node: normalizeNode(response.node),
  };
}

export async function updateOrganizationTreeNode(
  nodeId: string,
  payload: {
    title?: string;
    status?: SkillsNodeStatus;
    parentId?: string | null;
    sortOrder?: number;
    meta?: Record<string, unknown>;
    rules?: Record<string, unknown>;
    orgId?: string;
  },
) {
  const response = await apiFetch<OrganizationTreeNodeMutationResponse>(
    `/api/admin/organization-tree/nodes/${encodeURIComponent(nodeId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );

  return {
    orgId: response.orgId,
    node: normalizeNode(response.node),
  };
}

export async function deleteOrganizationTreeNode(nodeId: string, orgId?: string) {
  const query = orgId ? `?orgId=${encodeURIComponent(orgId)}` : '';
  return apiFetch<OrganizationTreeNodeDeleteResponse>(
    `/api/admin/organization-tree/nodes/${encodeURIComponent(nodeId)}${query}`,
    {
      method: 'DELETE',
    },
  );
}

export async function addOrganizationTreeRosterMember(
  nodeId: string,
  payload: { uid: string; role?: 'member' | 'supervisor' },
) {
  return apiFetch<BrainNodeRosterResponse>(
    `/api/admin/organization-tree/nodes/${encodeURIComponent(nodeId)}/team-roster`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function removeOrganizationTreeRosterMember(nodeId: string, uid: string, orgId?: string) {
  const query = orgId ? `?orgId=${encodeURIComponent(orgId)}` : '';
  return apiFetch<BrainNodeRosterResponse>(
    `/api/admin/organization-tree/nodes/${encodeURIComponent(nodeId)}/team-roster/${encodeURIComponent(uid)}${query}`,
    {
      method: 'DELETE',
    },
  );
}

const USERS_PAGE_SIZE = 20;
const MAX_USER_PAGES = 20;

async function fetchUsersByRole(role: 'admin' | 'agent'): Promise<AdminUserRecord[]> {
  const users: AdminUserRecord[] = [];

  for (let page = 0; page < MAX_USER_PAGES; page += 1) {
    const params = new URLSearchParams({
      q: '',
      role,
      page: String(page),
    });
    const response = await apiFetch<AdminUsersResponse>(`/api/admin/users?${params.toString()}`);
    const batch = response.users ?? [];
    users.push(...batch);
    if (batch.length < USERS_PAGE_SIZE) break;
  }

  return users;
}

export async function fetchOrganizationOperators(): Promise<SkillsTreeOperator[]> {
  const [admins, agents] = await Promise.all([fetchUsersByRole('admin'), fetchUsersByRole('agent')]);
  const deduped = new Map<string, SkillsTreeOperator>();
  for (const user of [...admins, ...agents]) {
    const key = user.id || user.email.trim().toLowerCase();
    if (!key) continue;
    if (user.role !== 'admin' && user.role !== 'agent') continue;
    const name = (user.name || '').trim() || user.email;
    const avatarUrl =
      (user.avatarUrl || '').trim() ||
      (user.photoURL || '').trim() ||
      (user.photoUrl || '').trim() ||
      (user.profileImageUrl || '').trim() ||
      (user.imageUrl || '').trim() ||
      undefined;
    deduped.set(key, {
      id: user.id,
      email: user.email,
      name,
      avatarUrl,
      role: user.role,
    });
  }

  return Array.from(deduped.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export async function fetchServiceRoutingOptions(): Promise<ServiceRoutingOption[]> {
  const response = await apiFetch<AdminCatalogConfigResponse>('/api/admin/services/config');
  const services = Array.isArray(response.services) ? response.services : [];

  return services
    .filter((service) => service && typeof service.code === 'string' && service.code.trim())
    .filter((service) => (service.status ?? '').toLowerCase() !== 'archived')
    .map((service) => {
      const rawTiers = Array.isArray(service.tiers) ? service.tiers : [];
      const filteredPrimaryTiers = rawTiers
        .filter((tier) => tier && typeof tier.skuId === 'string' && tier.skuId.trim())
        .filter((tier) => (tier.status ?? '').toLowerCase() !== 'archived')
        // Routing tier selector should reflect purchasable primary tiers only.
        .filter((tier) => tier.isPrimaryService !== false)
        // De-prioritize onboarding/auxiliary one-time tiers in routing UI.
        .filter((tier) => (tier.billingType ?? '').toString().toLowerCase() !== 'one_time');

      const bySku = new Map<string, ServiceRoutingTierOption>();
      for (const tier of filteredPrimaryTiers) {
        const skuId = tier.skuId.trim();
        if (!skuId) continue;
        const normalizedSku = skuId.toLowerCase();
        if (bySku.has(normalizedSku)) continue;
        bySku.set(normalizedSku, {
          skuId,
          name: (tier.name ?? tier.skuId).trim(),
        });
      }

      return {
        serviceCode: service.code.trim(),
        serviceTitle: (service.title ?? service.code).trim(),
        tiers: Array.from(bySku.values()).sort((a, b) => a.name.localeCompare(b.name)),
      };
    })
    .sort((a, b) => a.serviceTitle.localeCompare(b.serviceTitle));
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => asText(entry))
    .filter(Boolean);
}

export async function ensureProjectManagerAgentDefinition(): Promise<BrainAIAgentDefinition> {
  const response = await apiFetch<any>('/api/admin/ai/agents/system/project-manager', {
    method: 'POST',
  });
  return {
    id: asText(response?.id),
    code: asText(response?.code),
    title: asText(response?.title),
    description: asText(response?.description),
    roleSummary: asText(response?.roleSummary),
    systemPrompt: asText(response?.systemPrompt),
    defaultProvider: asText(response?.defaultProvider),
    defaultModel: asText(response?.defaultModel) || null,
    capabilities: asStringArray(response?.capabilities),
    allowedTools: asStringArray(response?.allowedTools),
    requiresHumanApproval: Boolean(response?.requiresHumanApproval),
    status: asText(response?.status),
    metadata: asRecord(response?.metadata),
  };
}

export async function ensureProgramManagerAgentDefinition(): Promise<BrainAIAgentDefinition> {
  const response = await apiFetch<any>('/api/admin/ai/agents/system/program-manager', {
    method: 'POST',
  });
  return {
    id: asText(response?.id),
    code: asText(response?.code),
    title: asText(response?.title),
    description: asText(response?.description),
    roleSummary: asText(response?.roleSummary),
    systemPrompt: asText(response?.systemPrompt),
    defaultProvider: asText(response?.defaultProvider),
    defaultModel: asText(response?.defaultModel) || null,
    capabilities: asStringArray(response?.capabilities),
    allowedTools: asStringArray(response?.allowedTools),
    requiresHumanApproval: Boolean(response?.requiresHumanApproval),
    status: asText(response?.status),
    metadata: asRecord(response?.metadata),
  };
}

export async function ensurePlanningAgentFamilyDefinitions(): Promise<
  BrainAIAgentDefinition[]
> {
  const response = await apiFetch<any>('/api/admin/ai/agents/system/planning-family', {
    method: 'POST',
  });
  const records = Array.isArray(response) ? response : Array.isArray(response?.agents) ? response.agents : [];
  return records
    .map((entry: unknown) => {
      const record = asRecord(entry);
      if (!record) return null;
      const id = asText(record.id);
      if (!id) return null;
      return {
        id,
        code: asText(record.code),
        title: asText(record.title),
        description: asText(record.description),
        roleSummary: asText(record.roleSummary),
        systemPrompt: asText(record.systemPrompt),
        defaultProvider: asText(record.defaultProvider),
        defaultModel: asText(record.defaultModel) || null,
        capabilities: asStringArray(record.capabilities),
        allowedTools: asStringArray(record.allowedTools),
        requiresHumanApproval: Boolean(record.requiresHumanApproval),
        status: asText(record.status),
        metadata: asRecord(record.metadata),
      } satisfies BrainAIAgentDefinition;
    })
    .filter(
      (entry: BrainAIAgentDefinition | null): entry is BrainAIAgentDefinition =>
        Boolean(entry),
    );
}

export async function listAIAgentAssignments(query: {
  agentCode?: string;
  nodeId?: string;
  orgId?: string;
  active?: boolean;
}): Promise<BrainAIAgentAssignment[]> {
  const params = new URLSearchParams();
  if (query.agentCode) params.set('agentCode', query.agentCode);
  if (query.nodeId) params.set('nodeId', query.nodeId);
  if (query.orgId) params.set('orgId', query.orgId);
  if (typeof query.active === 'boolean') params.set('active', String(query.active));
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const response = await apiFetch<any>(`/api/admin/ai/assignments${suffix}`);
  const assignments = Array.isArray(response?.assignments) ? response.assignments : [];
  return assignments
    .map((entry: unknown): BrainAIAgentAssignment | null => {
      const record = asRecord(entry);
      if (!record) return null;
      return mapAIAgentAssignment(record);
    })
    .filter(
      (entry: BrainAIAgentAssignment | null): entry is BrainAIAgentAssignment =>
        Boolean(entry),
    );
}

function mapAIAgentAssignment(entry: Record<string, unknown>): BrainAIAgentAssignment | null {
  const id = asText(entry.id);
  if (!id) return null;
  return {
    id,
    agentId: asText(entry.agentId),
    agentCode: asText(entry.agentCode),
    agentTitle: asText(entry.agentTitle),
    agentMetadata: asRecord(entry.agentMetadata),
    agentSurface: asText(entry.agentSurface) || null,
    planningRole: asText(entry.planningRole) || null,
    planningDomain: asText(entry.planningDomain) || null,
    scopeType: asText(entry.scopeType),
    orgId: asText(entry.orgId) || null,
    nodeId: asText(entry.nodeId) || null,
    serviceId: asText(entry.serviceId) || null,
    active: Boolean(entry.active),
    priority: Number.isFinite(Number(entry.priority)) ? Number(entry.priority) : 0,
    metadata: asRecord(entry.metadata),
  };
}

function mapProjectManagerApproval(
  entry: Record<string, unknown>,
): BrainProjectManagerApproval | null {
  const id = asText(entry.id);
  if (!id) return null;
  const mapPerson = (value: unknown) => {
    const record = asRecord(value);
    const userId = asText(record?.userId);
    const uid = asText(record?.uid);
    if (!userId || !uid) return null;
    return {
      userId,
      uid,
      name: asText(record?.name) || uid,
      email: asText(record?.email) || null,
    };
  };
  return {
    id,
    status: asText(entry.status),
    approvalType: asText(entry.approvalType),
    resourceType: asText(entry.resourceType) || null,
    resourceId: asText(entry.resourceId) || null,
    reviewNote: asText(entry.reviewNote) || null,
    reviewedAt: asText(entry.reviewedAt) || null,
    createdAt: asText(entry.createdAt),
    updatedAt: asText(entry.updatedAt),
    requestedBy: mapPerson(entry.requestedBy),
    reviewedBy: mapPerson(entry.reviewedBy),
    proposal: asRecord(entry.proposal),
    appliedSummary: asRecord(entry.appliedSummary),
    phaseKey: asText(entry.phaseKey) || null,
    phaseLabel: asText(entry.phaseLabel) || null,
    phaseAction: asText(entry.phaseAction) || null,
    nextPhaseKey: asText(entry.nextPhaseKey) || null,
    nextPhaseLabel: asText(entry.nextPhaseLabel) || null,
    scope: {
      nodeId: asText(asRecord(entry.scope)?.nodeId) || null,
      orgId: asText(asRecord(entry.scope)?.orgId) || null,
      orgName: asText(asRecord(entry.scope)?.orgName) || null,
      processTitle: asText(asRecord(entry.scope)?.processTitle) || null,
    },
  };
}

function mapProjectManagerRun(entry: Record<string, unknown>): BrainProjectManagerRun | null {
  const id = asText(entry.id);
  if (!id) return null;
  const approvalRecord = asRecord(entry.approval);
  const mapPerson = (value: unknown) => {
    const record = asRecord(value);
    const userId = asText(record?.userId);
    const uid = asText(record?.uid);
    if (!userId || !uid) return null;
    return {
      userId,
      uid,
      name: asText(record?.name) || uid,
      email: asText(record?.email) || null,
    };
  };
  const toolExecutions = Array.isArray(entry.toolExecutions)
    ? entry.toolExecutions
        .map((raw): BrainProjectManagerToolExecution | null => {
          const record = asRecord(raw);
          const toolId = asText(record?.id);
          if (!toolId) return null;
          return {
            id: toolId,
            toolName: asText(record?.toolName),
            status: asText(record?.status),
            inputPayload: asRecord(record?.inputPayload),
            outputPayload: asRecord(record?.outputPayload),
            errorText: asText(record?.errorText) || null,
            createdAt: asText(record?.createdAt),
            completedAt: asText(record?.completedAt) || null,
          };
        })
        .filter(
          (value): value is BrainProjectManagerToolExecution => Boolean(value),
        )
    : [];
  const childRuns = Array.isArray(entry.childRuns)
    ? entry.childRuns
        .map((raw): BrainProjectManagerChildRun | null => {
          const record = asRecord(raw);
          const runId = asText(record?.id);
          if (!runId) return null;
          return {
            id: runId,
            status: asText(record?.status),
            agentCode: asText(record?.agentCode) || null,
            agentTitle: asText(record?.agentTitle) || null,
            parentRunId: asText(record?.parentRunId) || null,
            rootRunId: asText(record?.rootRunId) || null,
            summary: asText(record?.summary) || null,
            errorText: asText(record?.errorText) || null,
            delegationMetadata: asRecord(record?.delegationMetadata),
            createdAt: asText(record?.createdAt),
            completedAt: asText(record?.completedAt) || null,
          };
        })
        .filter((value): value is BrainProjectManagerChildRun => Boolean(value))
    : [];
  return {
    id,
    agentCode: asText(entry.agentCode) || null,
    agentTitle: asText(entry.agentTitle) || null,
    status: asText(entry.status),
    parentRunId: asText(entry.parentRunId) || null,
    rootRunId: asText(entry.rootRunId) || null,
    provider: asText(entry.provider),
    model: asText(entry.model),
    inputTokens: typeof entry.inputTokens === 'number' ? entry.inputTokens : null,
    outputTokens: typeof entry.outputTokens === 'number' ? entry.outputTokens : null,
    startedAt: asText(entry.startedAt) || null,
    completedAt: asText(entry.completedAt) || null,
    createdAt: asText(entry.createdAt),
    updatedAt: asText(entry.updatedAt),
    nodeId: asText(entry.nodeId) || null,
    orgId: asText(entry.orgId) || null,
    orgName: asText(entry.orgName) || null,
    summary: asText(entry.summary) || null,
    errorText: asText(entry.errorText) || null,
    outputText: asText(entry.outputText) || null,
    delegationMetadata: asRecord(entry.delegationMetadata),
    promptSnapshot: asRecord(entry.promptSnapshot),
    responsePayload: asRecord(entry.responsePayload),
    requestedBy: mapPerson(entry.requestedBy),
    approval: approvalRecord ? mapProjectManagerApproval(approvalRecord) : null,
    toolExecutions,
    childRuns,
  };
}

export async function createAIAgentAssignment(payload: {
  agentId: string;
  scopeType: 'brain_node';
  nodeId: string;
  active: boolean;
  priority: number;
  metadata: Record<string, unknown>;
}): Promise<BrainAIAgentAssignment> {
  const response = await apiFetch<any>('/api/admin/ai/assignments', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const mapped = mapAIAgentAssignment(asRecord(response) ?? {});
  if (!mapped) {
    throw new Error('Unable to normalize the created AI agent assignment.');
  }
  return mapped;
}

export async function updateAIAgentAssignment(
  assignmentId: string,
  payload: {
    active?: boolean;
    priority?: number;
    metadata?: Record<string, unknown>;
  },
): Promise<BrainAIAgentAssignment> {
  const response = await apiFetch<any>(
    `/api/admin/ai/assignments/${encodeURIComponent(assignmentId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
  const mapped = mapAIAgentAssignment(asRecord(response) ?? {});
  if (!mapped) {
    throw new Error('Unable to normalize the updated AI agent assignment.');
  }
  return mapped;
}

export async function deleteAIAgentAssignment(
  assignmentId: string,
): Promise<{ ok: boolean; assignmentId: string }> {
  const response = await apiFetch<any>(
    `/api/admin/ai/assignments/${encodeURIComponent(assignmentId)}`,
    {
      method: 'DELETE',
    },
  );
  return {
    ok: response?.ok === true,
    assignmentId: asText(response?.assignmentId) || assignmentId,
  };
}

export async function fetchBrainKBaseProcessOptions(): Promise<BrainKBaseProcessOption[]> {
  const response = await apiFetch<any>('/api/admin/kbase/processes');
  const processes = Array.isArray(response?.processes) ? response.processes : [];
  return processes
    .map((entry: unknown): BrainKBaseProcessOption | null => {
      const record = asRecord(entry);
      if (!record) return null;
      const id = asText(record.id);
      const title = asText(record.title);
      if (!id || !title) return null;
      return {
        id,
        title,
        status: asText(record.status) || 'Draft',
        processType: asText(record.processType) || 'internal',
        serviceCode: asText(record.serviceCode) || null,
        serviceLabel: asText(record.serviceLabel) || null,
        category: asText(record.category) || null,
        subcategories: asStringArray(record.subcategories),
      };
    })
    .filter(
      (entry: BrainKBaseProcessOption | null): entry is BrainKBaseProcessOption =>
        Boolean(entry),
    );
}

export async function listProjectManagerRuns(query: {
  nodeId: string;
  orgId?: string;
  approvalStatus?: string;
  limit?: number;
}): Promise<BrainProjectManagerRun[]> {
  const params = new URLSearchParams({
    nodeId: query.nodeId,
  });
  if (query.orgId) params.set('orgId', query.orgId);
  if (query.approvalStatus) params.set('approvalStatus', query.approvalStatus);
  if (typeof query.limit === 'number' && Number.isFinite(query.limit)) {
    params.set('limit', String(Math.max(1, Math.trunc(query.limit))));
  }
  const response = await apiFetch<any>(
    `/api/admin/ai/project-manager/runs?${params.toString()}`,
  );
  const runs = Array.isArray(response?.runs) ? response.runs : [];
  return runs
    .map((entry: unknown) => {
      const record = asRecord(entry);
      return record ? mapProjectManagerRun(record) : null;
    })
    .filter((entry: BrainProjectManagerRun | null): entry is BrainProjectManagerRun => Boolean(entry));
}

export async function createProjectManagerRun(payload: {
  nodeId: string;
  orgId?: string | null;
  projectId?: string | null;
  buildPath?: 'existing_project' | 'new_project' | null;
  meetingNoteIds?: string[];
  instruction?: string;
}): Promise<BrainProjectManagerRun> {
  const response = await apiFetch<any>('/api/admin/ai/project-manager/runs', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const runRecord = asRecord(response?.run);
  const mapped = runRecord ? mapProjectManagerRun(runRecord) : null;
  if (!mapped) {
    throw new Error('Unable to normalize the Project Manager run.');
  }
  return mapped;
}

export async function streamProjectManagerRun(
  payload: {
    nodeId: string;
    orgId?: string | null;
    projectId?: string | null;
    buildPath?: 'existing_project' | 'new_project' | null;
    meetingNoteIds?: string[];
    instruction?: string;
  },
  options: {
    signal?: AbortSignal;
    onEvent: (event: BrainProjectManagerStreamEvent) => void;
  },
) {
  const response = await apiFetchStream('/api/admin/ai/project-manager/runs/stream', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal: options.signal,
    headers: {
      Accept: 'application/x-ndjson',
    },
  });

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('PM planning stream unavailable.');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  const emitLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const raw = JSON.parse(trimmed) as Record<string, unknown>;
    const type = asText(raw.type);
    let event: BrainProjectManagerStreamEvent | null = null;
    if (type === 'status') {
      event = {
        type: 'status',
        stage: asText(raw.stage) || 'planning',
        message: asText(raw.message) || 'Project Manager update',
        detail: asText(raw.detail) || null,
      };
    } else if (type === 'complete') {
      const responseRecord = asRecord(raw.response);
      const runRecord = responseRecord ? asRecord(responseRecord.run) : null;
      const run = runRecord ? mapProjectManagerRun(runRecord) : null;
      if (run) {
        event = {
          type: 'complete',
          response: { run },
        };
      }
    } else if (type === 'error') {
      event = {
        type: 'error',
        error: asText(raw.error) || 'Project Manager planning failed.',
      };
    }
    if (!event) return;
    options.onEvent(event);
    if (event.type === 'error') {
      throw new Error(event.error || 'Project Manager planning failed.');
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });

    let newlineIndex = buffer.indexOf('\n');
    while (newlineIndex >= 0) {
      const line = buffer.slice(0, newlineIndex);
      buffer = buffer.slice(newlineIndex + 1);
      emitLine(line);
      newlineIndex = buffer.indexOf('\n');
    }

    if (done) break;
  }

  if (buffer.trim()) {
    emitLine(buffer);
  }
}

export async function reviewProjectManagerApproval(
  approvalId: string,
  payload: {
    status: 'approved' | 'rejected' | 'canceled';
    reviewNote?: string;
  },
): Promise<BrainProjectManagerApproval> {
  const response = await apiFetch<any>(
    `/api/admin/ai/project-manager/approvals/${encodeURIComponent(approvalId)}/review`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  const approvalRecord = asRecord(response?.approval);
  const mapped = approvalRecord ? mapProjectManagerApproval(approvalRecord) : null;
  if (!mapped) {
    throw new Error('Unable to normalize the Project Manager approval.');
  }
  return mapped;
}

export async function applyProjectManagerApproval(
  approvalId: string,
): Promise<BrainProjectManagerApproval> {
  const response = await apiFetch<any>(
    `/api/admin/ai/project-manager/approvals/${encodeURIComponent(approvalId)}/apply`,
    {
      method: 'POST',
    },
  );
  const approvalRecord = asRecord(response?.approval);
  const mapped = approvalRecord ? mapProjectManagerApproval(approvalRecord) : null;
  if (!mapped) {
    throw new Error('Unable to normalize the applied Project Manager approval.');
  }
  return mapped;
}

export async function fetchProjectManagerNodeClients(
  teamNodeId: string,
): Promise<BrainProjectManagerNodeClient[]> {
  const response = await apiFetch<any>(
    `/api/projects/node-client-summary?teamNodeId=${encodeURIComponent(teamNodeId)}`,
  );
  const clients = Array.isArray(response?.clients) ? response.clients : [];
  return clients
    .map((entry: unknown) => {
      const record = asRecord(entry);
      const orgId = asText(record?.orgId);
      const name = asText(record?.name);
      if (!orgId || !name) return null;
      return {
        orgId,
        name,
        ownerEmail: asText(record?.ownerEmail) || null,
        assignedServiceTeam: asText(record?.assignedServiceTeam) || null,
        assignedServiceTeams: asStringArray(record?.assignedServiceTeams),
        primaryServiceActive:
          typeof record?.primaryServiceActive === 'boolean'
            ? record.primaryServiceActive
            : null,
        serviceLabels: asStringArray(record?.serviceLabels),
        projectCountOnNode: Number(record?.projectCountOnNode) || 0,
        latestProjectUpdatedAt: asText(record?.latestProjectUpdatedAt) || null,
        latestRunAt: asText(record?.latestRunAt) || null,
        latestSubscriptionUpdatedAt:
          asText(record?.latestSubscriptionUpdatedAt) || null,
        sources: asStringArray(record?.sources),
      };
    })
    .filter(Boolean) as BrainProjectManagerNodeClient[];
}
